import { useState } from "react";
import { TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Analytics } from "@/lib/analytics";
import { signupUrl } from "@/lib/productApp";
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from "framer-motion";
import { useEffect, useRef } from "react";
import type { AdsMode } from "./AdsModeSelector";

const AnimatedNumber = ({ value }: { value: number }) => {
  const motionValue = useMotionValue(0);
  const display = useTransform(motionValue, (v) =>
    Math.round(v).toLocaleString("fr-FR")
  );
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const controls = animate(motionValue, value, { duration: 0.6, ease: "easeOut" });
    return controls.stop;
  }, [value, motionValue]);

  return <motion.span ref={ref}>{display}</motion.span>;
};

const AdsROISlider = ({ mode }: { mode: AdsMode }) => {
  const [venteSlider, setVenteSlider] = useState(2);
  const [locationSlider, setLocationSlider] = useState(35);

  const isVente = mode === "vente";

  // Vente: missedCalls * 52 * 0.04 * 8000
  const venteAnnualLoss = Math.round(venteSlider * 52 * 0.04 * 8000);

  // Location: calls * 0.85 * 5 min => minutes => hours * 35€
  const locationMinutes = locationSlider * 0.85 * 5;
  const locationHours = locationMinutes / 60;
  const locationCost = Math.round(locationHours * 35);

  return (
    <section className="section-padding bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          className="max-w-2xl mx-auto text-center mb-12"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <TrendingUp className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
            Chaque appel manqué a un prix.
          </h2>
        </motion.div>

        <div className="max-w-xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              className="card-stripe p-8 md:p-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {isVente ? (
                <>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Appels manqués par semaine
                  </label>
                  <div className="flex items-center gap-4 mb-2">
                    <Slider
                      value={[venteSlider]}
                      onValueChange={(v) => setVenteSlider(v[0])}
                      min={1}
                      max={20}
                      step={1}
                      className="flex-1"
                    />
                    <motion.span
                      className="text-2xl font-bold text-primary w-12 text-right tabular-nums"
                      key={venteSlider}
                      initial={{ scale: 1.3, opacity: 0.6 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {venteSlider}
                    </motion.span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-8">
                    Base : taux de conversion 4 % · commission moyenne 8 000 €
                  </p>

                  <div className="text-center p-6 rounded-xl bg-destructive/5 border border-destructive/20">
                    <p className="text-sm text-muted-foreground mb-1">Perte annuelle estimée</p>
                    <p className="text-4xl md:text-5xl font-bold text-destructive tabular-nums">
                      <AnimatedNumber value={venteAnnualLoss} /> €
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      C'est ce que la concurrence encaisse à votre place.
                    </p>
                  </div>

                  <p className="text-center text-lg font-semibold text-foreground mt-8">
                    Un seul mandat récupéré rembourse Immovox pour l'année entière.
                  </p>

                  <div className="text-center mt-6">
                    <Button
                      size="lg"
                      className="h-14 px-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-warm group"
                      asChild
                    >
                      <a
                        href={signupUrl()}
                        onClick={() => Analytics.ctaClicked("securiser_mandats", "ads_roi_slider")}
                      >
                        <TrendingUp className="w-5 h-5 mr-2" />
                        Stopper les pertes
                      </a>
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Appels reçus par annonce publiée
                  </label>
                  <div className="flex items-center gap-4 mb-2">
                    <Slider
                      value={[locationSlider]}
                      onValueChange={(v) => setLocationSlider(v[0])}
                      min={10}
                      max={100}
                      step={5}
                      className="flex-1"
                    />
                    <motion.span
                      className="text-2xl font-bold text-primary w-12 text-right tabular-nums"
                      key={locationSlider}
                      initial={{ scale: 1.3, opacity: 0.6 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {locationSlider}
                    </motion.span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-8">
                    Base : 85 % d'appels non qualifiés · 5 min par appel
                  </p>

                  <div className="text-center p-6 rounded-xl bg-destructive/5 border border-destructive/20">
                    <p className="text-sm text-muted-foreground mb-1">Temps perdu par annonce</p>
                    <p className="text-4xl md:text-5xl font-bold text-destructive tabular-nums">
                      {Math.floor(locationHours)}h{String(Math.round((locationHours % 1) * 60)).padStart(2, "0")}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      à traiter des appels qui ne mèneront nulle part.
                    </p>
                  </div>

                  <p className="text-center text-lg font-semibold text-foreground mt-8">
                    Immovox qualifie pour vous. Vous ne gérez que les bons dossiers.
                  </p>

                  <div className="text-center mt-6">
                    <Button
                      size="lg"
                      className="h-14 px-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-warm group"
                      asChild
                    >
                      <a
                        href={signupUrl()}
                        onClick={() => Analytics.ctaClicked("activer_bouclier", "ads_roi_slider")}
                      >
                        <TrendingUp className="w-5 h-5 mr-2" />
                        Récupérer mon temps
                      </a>
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default AdsROISlider;
